import { Box, Grid, Card, CardContent, Typography, Link, Button, Chip, Stack } from '@mui/material';
import {
  Launch as LaunchIcon,
  TrendingUp,
  Analytics,
  BarChart,
  MoveUp,
  AirplanemodeActive,
  Gavel,
} from '@mui/icons-material';
import CadastreToolCard from '@/components/ui/CadastreToolCard.tsx';
import { PageHeader } from '@/components/ui/PageHeader.tsx';
import { useEffect } from 'react';
import { useGetMe, useMarkQuickGuideAction } from '@/lib/api/me';

const tools = [
  {
    title: 'Crecimiento poblacional',
    description:
      'Explora la evolución de la población y los desequilibrios territoriales para identificar áreas de crecimiento.',
    url: 'https://educativo.ign.es/atlas-didactico/poblacion-bach/crecimiento_poblacional_y_desequilibrios_territoriales.html',
    category: 'Demografía',
    icon: <TrendingUp />,
  },
  {
    title: 'Renta per cápita',
    description:
      'Consulta los últimos datos de renta per cápita por región para evaluar el poder adquisitivo.',
    url: 'https://www.ine.es/dyngs/INEbase/es/operacion.htm?c=Estadistica_C&cid=1254736177088&menu=ultiDatos&idp=1254735976608',
    category: 'Economía',
    icon: <Analytics />,
  },
  {
    title: 'Compraventa de viviendas',
    description:
      'Accede a las estadísticas oficiales de transacciones inmobiliarias por tipo y región.',
    url: 'https://www.transportes.gob.es/el-ministerio/informacion-estadistica/vivienda-y-actuaciones-urbanas/estadisticas/transacciones-inmobiliarias-compraventa',
    category: 'Mercado inmobiliario',
    icon: <BarChart />,
  },
  {
    title: 'Movilidad residencial',
    description:
      'Consulta las altas y bajas de población entre municipios y provincias para identificar flujos migratorios.',
    url: 'https://www.ine.es/dyngs/INEbase/es/operacion.htm?c=Estadistica_C&cid=1254736177013&idp=1254734710990&menu=resultados',
    category: 'Demografía',
    icon: <MoveUp />,
  },
  {
    title: 'Ocupación turística',
    description:
      'Explora los datos de pernoctaciones y ocupación hotelera para analizar la demanda turística.',
    url: 'https://www.ine.es/dyngs/INEbase/es/operacion.htm?c=Estadistica_C&cid=1254736177015&idp=1254735576863&menu=ultiDatos',
    category: 'Turismo',
    icon: <AirplanemodeActive />,
  },

  {
    title: 'Catastro',
    description:
      'Consulta referencias catastrales, características físicas y valor de los inmuebles en España.',
    url: 'https://www1.sedecatastro.gob.es/',
    category: 'Registro',
    icon: <Gavel />,
  },
];

export default function ToolsPage() {
  const { data: user } = useGetMe();
  const { mutate: markQuickGuideAction } = useMarkQuickGuideAction();

  useEffect(() => {
    if (user) {
      markQuickGuideAction('tools');
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box>
      <PageHeader
        title="Herramientas para Inversores"
        subtitle="Recursos especializados para el análisis de mercado inmobiliario"
      />
      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ mb: 4, lineHeight: 1.6, maxWidth: 800 }}
      >
        Accede a herramientas y datos oficiales que te ayudarán a tomar decisiones de inversión más
        informadas basadas en indicadores demográficos y económicos clave.
      </Typography>

      {/* Cadastre Tool */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} lg={8}>
          <CadastreToolCard />
        </Grid>
      </Grid>

      {/* External Tools Grid */}
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: 'primary.main' }}>
        Herramientas Externas
      </Typography>
      <Grid container spacing={3}>
        {tools.map((tool) => (
          <Grid item xs={12} md={6} key={tool.title}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'all 0.3s ease-in-out',
                cursor: 'pointer',
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: 6,
                  borderColor: 'primary.main',
                  '& .tool-icon': {
                    color: 'primary.main',
                    transform: 'scale(1.1)',
                  },
                  '& .launch-button': {
                    backgroundColor: 'primary.main',
                    color: 'white',
                  },
                },
              }}
            >
              <CardContent sx={{ flexGrow: 1, p: 3 }}>
                <Stack spacing={2}>
                  {/* Icon and Category */}
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Box
                      className="tool-icon"
                      sx={{
                        color: 'text.secondary',
                        transition: 'all 0.3s ease-in-out',
                      }}
                    >
                      {tool.icon}
                    </Box>
                    <Chip
                      label={tool.category}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderColor: 'primary.main',
                        color: 'primary.main',
                        fontWeight: 500,
                      }}
                    />
                  </Stack>

                  {/* Title */}
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    {tool.title}
                  </Typography>

                  {/* Description */}
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ lineHeight: 1.6, flexGrow: 1 }}
                  >
                    {tool.description}
                  </Typography>

                  {/* Action Button */}
                  <Button
                    className="launch-button"
                    component={Link}
                    href={tool.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="outlined"
                    endIcon={<LaunchIcon />}
                    sx={{
                      mt: 2,
                      alignSelf: 'flex-start',
                      textDecoration: 'none',
                      transition: 'all 0.3s ease-in-out',
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      '&:hover': {
                        textDecoration: 'none',
                      },
                    }}
                  >
                    Acceder a la herramienta
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Footer Note */}
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          💡 Todas las herramientas se abren en una nueva pestaña y provienen de fuentes oficiales
        </Typography>
      </Box>
    </Box>
  );
}
